import {
  Grid,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  SendPush,
  GetPushList,
  UpdatePush,
  DeletePush,
} from "../../redux/api/pushnotification.api";

const PushNotifications = () => {
  const [pushList, setPushList] = useState([]);
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [message, setMessage] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSubTitle, setEditSubTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editLink, setEditLink] = useState("");

  const theme = useTheme();
  const cardBg =
    theme.palette.background?.card ??
    theme.palette.background?.paper ??
    theme.palette.background?.default;
  const textPrimary = theme.palette.text.primary;
  const isDark = theme.palette.mode === "dark";
  const inputBg = isDark
    ? (theme.palette.grey?.[800] ?? "#2d2d2d")
    : theme.palette.background.paper;
  const inputBorder = theme.palette.divider;
  const inputTextColor = theme.palette.text.primary;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: `${inputBg} !important`,
      color: inputTextColor,
      "& fieldset": { borderColor: inputBorder },
      "&:hover fieldset": { borderColor: "primary.main" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
      "& input, & textarea": {
        color: `${inputTextColor} !important`,
        WebkitTextFillColor: `${inputTextColor} !important`,
      },
      "& input::placeholder, & textarea::placeholder": {
        opacity: 0.7,
        color: `${inputTextColor} !important`,
      },
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
  };

  const fetchPushList = async () => {
    const res = await GetPushList();
    if (res?.status === 200 && res?.data) setPushList(res.data);
  };

  useEffect(() => {
    fetchPushList();
  }, []);

  const handleSendPush = async () => {
    const trimmedTitle = (title || "").trim();
    if (!trimmedTitle) {
      setMessage("Title is required");
      return;
    }
    setMessage("");
    const res = await SendPush({
      type: "news",
      title: trimmedTitle,
      subTitle: (subTitle || "").trim(),
      body: (body || "").trim(),
      link: (link || "").trim(),
      lang: "en",
    });
    if (res?.status === 200) {
      setMessage("Push sent successfully");
      setTitle("");
      setSubTitle("");
      setBody("");
      setLink("");
      fetchPushList();
    } else {
      setMessage(res?.message || "Failed to send push");
    }
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setEditTitle(typeof row.title === "string" && row.title.startsWith("{") ? row.title : row.title || "");
    setEditSubTitle(row.subtitle || "");
    setEditBody(row.body || "");
    setEditLink(row.link || "");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingId(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const res = await UpdatePush({
      id: editingId,
      title: editTitle,
      subTitle: editSubTitle,
      body: editBody,
      link: editLink,
    });
    if (res?.status === 200) {
      setMessage("Push updated");
      fetchPushList();
      closeEdit();
    } else {
      setMessage(res?.message || "Update failed");
    }
  };

  const handleDeletePush = async (id) => {
    if (!window.confirm("Delete this push record? (Already-sent notifications are not revoked.)")) return;
    const res = await DeletePush(id);
    if (res?.status === 200) {
      setMessage("Push deleted");
      fetchPushList();
    } else {
      setMessage(res?.message || "Delete failed");
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleDateString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const parseTitle = (t) => {
    if (typeof t !== "string") return t || "";
    try {
      const parsed = JSON.parse(t);
      return typeof parsed === "object" ? parsed.en || parsed[Object.keys(parsed)[0]] || t : String(parsed);
    } catch {
      return t;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={3} pb={3}>
        {/* Top bar: Send button + message */}
        <MDBox py={1} display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <MDButton
            color="info"
            variant="contained"
            disabled={!(title || "").trim()}
            onClick={handleSendPush}
          >
            Send new push
          </MDButton>
          {message && (
            <Typography variant="body2" color={message.includes("success") || message.includes("updated") || message.includes("deleted") ? "success.main" : "text.secondary"}>
              {message}
            </Typography>
          )}
        </MDBox>

        {/* Send new push form */}
        <MDBox mb={4}>
          <Typography variant="h6" sx={{ color: textPrimary, mb: 2 }}>
            New push notification
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Sub title"
                value={subTitle}
                onChange={(e) => setSubTitle(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Link (optional)"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Body"
                multiline
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Sent pushes list */}
        <MDBox mb={4}>
          <Typography variant="h6" sx={{ color: textPrimary, mb: 2 }}>
            Sent pushes (edit or delete record only; does not unsend)
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: cardBg }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: textPrimary, fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ color: textPrimary, fontWeight: 600 }}>Subtitle</TableCell>
                  <TableCell sx={{ color: textPrimary, fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="right" sx={{ color: textPrimary, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pushList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: "text.secondary", py: 3 }}>
                      No sent pushes yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  pushList.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ color: textPrimary }}>{parseTitle(row.title)}</TableCell>
                      <TableCell sx={{ color: textPrimary }}>{row.subtitle || "—"}</TableCell>
                      <TableCell sx={{ color: textPrimary }}>{formatDate(row.created_at)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "primary.main" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeletePush(row.id)} sx={{ color: "error.main" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </MDBox>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: cardBg, color: textPrimary }}>Edit push (record only)</DialogTitle>
        <DialogContent sx={{ bgcolor: cardBg }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Sub title"
                value={editSubTitle}
                onChange={(e) => setEditSubTitle(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Link"
                value={editLink}
                onChange={(e) => setEditLink(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Body"
                multiline
                rows={3}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ bgcolor: cardBg, color: textPrimary }}>
          <MDButton variant="outlined" onClick={closeEdit}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleSaveEdit}>
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default PushNotifications;
